import React, { useMemo, useState } from 'react';
import { Alert, App, Button, Card, Col, Row, Select, Space, Statistic, Steps, Table, Tag, Typography, Upload } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { CANDIDATE_SOURCES, exportRows, recruitErrorMessage } from './shared/recruitUi';

const { Text } = Typography;

/** Spreadsheet header → API field. Headers are matched case/space-insensitively. */
const COLUMN_ALIASES: Record<string, string> = {
  firstname: 'first_name',
  first: 'first_name',
  lastname: 'last_name',
  last: 'last_name',
  surname: 'last_name',
  email: 'email',
  emailaddress: 'email',
  phone: 'phone',
  mobile: 'phone',
  phonenumber: 'phone',
  nationality: 'nationality',
  location: 'current_location',
  currentlocation: 'current_location',
  source: 'source',
  agency: 'agency_name',
  agencyname: 'agency_name',
  skills: 'skills',
  employer: 'current_employer',
  currentemployer: 'current_employer',
  title: 'current_title',
  currenttitle: 'current_title',
  jobtitle: 'current_title',
  experience: 'experience_years',
  experienceyears: 'experience_years',
  yearsofexperience: 'experience_years',
};

const TEMPLATE_COLUMNS: Array<[string, string]> = [
  ['first_name', 'First name'],
  ['last_name', 'Last name'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['nationality', 'Nationality'],
  ['current_location', 'Location'],
  ['current_title', 'Current title'],
  ['current_employer', 'Current employer'],
  ['experience_years', 'Experience years'],
  ['skills', 'Skills'],
  ['source', 'Source'],
  ['agency_name', 'Agency'],
];

const STATUS_COLOR: Record<string, string> = {
  Ready: 'blue',
  Created: 'green',
  Linked: 'cyan',
  Duplicate: 'orange',
  Error: 'red',
};

async function parseSheet(file: File): Promise<Array<Record<string, unknown>>> {
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) return [];
  const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
  return raw.map((r) => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(r)) {
      const field = COLUMN_ALIASES[k.toLowerCase().replace(/[^a-z]/g, '')];
      if (field && v !== '') out[field] = typeof v === 'string' ? v.trim() : v;
    }
    if (!out.first_name && !out.last_name) {
      const full = Object.entries(r).find(([k]) => /^(full)?name$/i.test(k.replace(/\s/g, '')))?.[1];
      if (full) {
        const parts = String(full).trim().split(/\s+/);
        out.first_name = parts.shift();
        out.last_name = parts.join(' ') || '.';
      }
    }
    return out;
  });
}

const CandidateImportPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [fileName, setFileName] = useState<string>();
  const [jobId, setJobId] = useState<string>();
  const [source, setSource] = useState<string>('Import');
  const [tags, setTags] = useState<string[]>([]);
  const [preview, setPreview] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const { data: jobs } = useQuery({
    queryKey: hrmsKeys.jobs({ status: 'Published,Draft', import: 1 }),
    queryFn: () => hrmsService.pageRecruitJobs({ page: 1, limit: 200, status: 'Published' }),
  });
  const { data: tagOptions = [] } = useQuery({ queryKey: hrmsKeys.candidateTags(), queryFn: () => hrmsService.listCandidateTags() });

  const step = result ? 2 : preview ? 1 : 0;
  const report = result || preview;
  const summary = useMemo(() => report?.summary || {}, [report]);

  const run = async (dryRun: boolean) => {
    setBusy(true);
    try {
      const r = await hrmsService.importCandidates({ rows, job_posting_id: jobId, source, tags, dry_run: dryRun });
      if (dryRun) setPreview(r);
      else {
        setResult(r);
        await queryClient.invalidateQueries({ queryKey: hrmsKeys.candidates() });
        await queryClient.invalidateQueries({ queryKey: hrmsKeys.applications() });
        message.success('Import finished');
      }
    } catch (e: any) {
      message.error(recruitErrorMessage(e, 'Import failed'));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setRows([]);
    setFileName(undefined);
    setPreview(null);
    setResult(null);
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Import candidates"
          subtitle="Bulk-load CVs from job boards, agencies or career fairs. Duplicates are matched on email and phone."
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/candidates')}>
                Back
              </Button>
              <Button icon={<DownloadOutlined />} onClick={() => exportRows('candidate-import-template', TEMPLATE_COLUMNS, [])}>
                Template
              </Button>
            </Space>
          }
        />
      }
    >
      <Steps current={step} style={{ maxWidth: 720, marginBottom: 24 }} items={[{ title: 'Upload' }, { title: 'Preview' }, { title: 'Done' }]} />

      {step === 0 && (
        <Card style={{ maxWidth: 960 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Text strong>Attach to job (optional)</Text>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: '100%', marginTop: 4, marginBottom: 12 }}
                placeholder="Create applications for this job"
                value={jobId}
                onChange={setJobId}
                options={(jobs?.rows || []).map((j: any) => ({ value: j.id, label: `${j.job_number} — ${j.title}` }))}
              />
            </Col>
            <Col xs={24} md={8}>
              <Text strong>Default source</Text>
              <Select
                style={{ width: '100%', marginTop: 4, marginBottom: 12 }}
                value={source}
                onChange={setSource}
                options={CANDIDATE_SOURCES.map((v) => ({ value: v, label: v }))}
              />
            </Col>
            <Col xs={24} md={8}>
              <Text strong>Tag everyone with</Text>
              <Select
                mode="tags"
                style={{ width: '100%', marginTop: 4, marginBottom: 12 }}
                value={tags}
                onChange={setTags}
                options={tagOptions.map((t) => ({ value: t, label: t }))}
              />
            </Col>
          </Row>
          <Upload.Dragger
            accept=".xlsx,.xls,.csv"
            maxCount={1}
            showUploadList={false}
            beforeUpload={async (file) => {
              try {
                const parsed = await parseSheet(file);
                if (!parsed.length) {
                  message.warning('No rows found in the first sheet');
                  return false;
                }
                if (parsed.length > 2000) message.warning('Only the first 2,000 rows will be imported');
                setRows(parsed.slice(0, 2000));
                setFileName(file.name);
              } catch {
                message.error('Could not read this file');
              }
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{fileName ? `${fileName} — ${rows.length} row(s)` : 'Drop an Excel or CSV file here'}</p>
            <p className="ant-upload-hint">Columns: First name, Last name, Email, Phone (email or phone required), plus optional profile columns.</p>
          </Upload.Dragger>
          <Space style={{ marginTop: 16 }}>
            <Button type="primary" disabled={!rows.length} loading={busy} onClick={() => run(true)}>
              Check {rows.length || ''} row(s)
            </Button>
          </Space>
        </Card>
      )}

      {report && (
        <>
          <Row gutter={16} style={{ marginBottom: 16, maxWidth: 960 }}>
            {Object.entries(summary).map(([k, v]) => (
              <Col key={k} xs={12} md={4}>
                <Card size="small">
                  <Statistic title={k} value={v as number} />
                </Card>
              </Col>
            ))}
          </Row>
          {step === 1 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16, maxWidth: 960 }}
              message="Nothing has been saved yet"
              description={
                jobId
                  ? 'Ready rows become new candidates with an application; duplicates are linked to the job using the existing record. Error rows are skipped.'
                  : 'Ready rows become new candidates. Duplicates and error rows are skipped.'
              }
              action={
                <Space direction="vertical">
                  <Button type="primary" loading={busy} disabled={!summary.Ready && !(jobId && summary.Duplicate)} onClick={() => run(false)}>
                    Import
                  </Button>
                  <Button onClick={reset}>Start over</Button>
                </Space>
              }
            />
          )}
          {step === 2 && (
            <Space style={{ marginBottom: 16 }}>
              <Button type="primary" onClick={() => navigate('/hr/candidates')}>
                Go to candidates
              </Button>
              <Button onClick={reset}>Import another file</Button>
            </Space>
          )}
          <Table
            rowKey="row"
            size="small"
            dataSource={report.rows}
            pagination={{ pageSize: 50 }}
            columns={[
              { title: 'Row', dataIndex: 'row', width: 70 },
              {
                title: 'Name',
                dataIndex: 'name',
                render: (v, r: any) => (r.candidate_id ? <a onClick={() => navigate(`/hr/candidates/${r.candidate_id}`)}>{v}</a> : v),
              },
              { title: 'Result', dataIndex: 'status', width: 110, render: (s) => <Tag color={STATUS_COLOR[s]}>{s}</Tag> },
              { title: 'Message', dataIndex: 'message' },
            ]}
          />
        </>
      )}
    </WorkspaceLayout>
  );
};

export default CandidateImportPage;
